import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Check } from "lucide-react";
import api from "@/lib/api";

/**
 * Phase 8.1.b — Controlled stock picker used inside the Add/Edit
 * Holding dialog. Reuses the SAME `/api/search` endpoint that powers
 * the main Stockaily search/autocomplete on the homepage and navbar,
 * so suggestion quality is identical (ticker, partial ticker, full
 * company name, partial company name all work).
 *
 * Unlike :file:`StockSearch.js`, this picker is a CONTROLLED input —
 * it does NOT navigate anywhere. It emits :prop:`onSelect(result)`
 * when the user picks a suggestion (click, tap, or Enter on an
 * arrow-key-highlighted row), and :prop:`onClear()` when they clear
 * the selection.
 *
 * Validation contract:
 *   * The picker considers the value "valid" ONLY when the user has
 *     selected a suggestion from the dropdown. Free-typed text that
 *     doesn't resolve to a suggestion is NEVER auto-resolved — the
 *     parent receives `selected=null` until a suggestion is picked.
 *   * This prevents `APPLE` (no real ticker) being stored as a
 *     ticker by accident.
 *
 * Accessibility: listbox / option roles, keyboard arrow nav, Enter
 * commits, Escape closes the dropdown.
 *
 * Mobile: the dropdown anchors to the parent container so it never
 * clips outside the Shadcn Dialog body.
 */
export default function StockTickerPicker({
  value = null,
  onSelect,
  onClear,
  disabled = false,
  autoFocus = true,
  placeholder = "Search ticker or company (AAPL, Apple, Nvidia…)",
  testIdPrefix = "portfolio-ticker-picker",
  emptyMessage = "No matches.",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // If a value is provided (edit mode pre-fill), show its label.
  const selected = value || null;

  // Close on outside click / tap.
  useEffect(() => {
    function onPointerDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, []);

  // Debounced search against the shared /api/search endpoint.
  useEffect(() => {
    if (selected) return undefined; // don't search while a selection is locked
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        if (!query.trim()) {
          // Show popular tickers as gentle defaults — same UX as the
          // main StockSearch component.
          const res = await api.get("/popular", { params: { limit: 6 } });
          if (!cancelled) {
            setResults(res?.data?.results || []);
            setHighlight(0);
          }
          return;
        }
        const res = await api.get("/search", { params: { q: query, limit: 8 } });
        if (!cancelled) {
          setResults(res?.data?.results || []);
          setHighlight(0);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const tmr = setTimeout(run, 120);
    return () => { cancelled = true; clearTimeout(tmr); };
  }, [query, selected]);

  const commit = (item) => {
    if (!item || !item.ticker) return;
    setOpen(false);
    setQuery("");
    onSelect?.({
      ticker: String(item.ticker).toUpperCase(),
      name: item.name || item.company_name || null,
      exchange: item.exchange || null,
      currency: item.currency || null,
      sector: item.sector || null,
    });
  };

  const clearSelection = () => {
    onClear?.();
    setQuery("");
    setOpen(true);
    // Refocus so the user can keep typing immediately.
    setTimeout(() => inputRef.current?.focus?.(), 0);
  };

  const onKeyDown = (e) => {
    if (disabled || selected) return;
    if (!open) setOpen(true);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min((results?.length || 1) - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[highlight];
      if (item) commit(item);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Show the locked-selection chip rather than the input when a value
  // has been committed. The user can clear it explicitly via the X.
  if (selected) {
    return (
      <div
        ref={containerRef}
        data-testid={`${testIdPrefix}-selected`}
        className="flex items-center justify-between gap-2 h-11 rounded-xl border border-[#3DEBFF]/35 bg-[#3DEBFF]/[0.06] px-3"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Check className="w-4 h-4 text-[#3DEBFF] shrink-0" />
          <span className="font-mono font-medium text-sm tracking-wider">{selected.ticker}</span>
          {selected.name ? (
            <span className="text-sm text-muted-foreground truncate">— {selected.name}</span>
          ) : null}
          {selected.exchange ? (
            <span className="text-[11px] text-muted-foreground/70 hidden sm:inline">· {selected.exchange}</span>
          ) : null}
        </div>
        {disabled ? null : (
          <button
            type="button"
            aria-label="Clear selected stock"
            data-testid={`${testIdPrefix}-clear-button`}
            onClick={clearSelection}
            className="text-muted-foreground hover:text-foreground rounded-md p-1"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative" data-testid={`${testIdPrefix}-root`}>
      <div className="flex items-center h-11 rounded-xl border border-border/70 bg-background px-3 focus-within:border-[#3DEBFF]/55 focus-within:ring-2 focus-within:ring-[#3DEBFF]/20 transition-colors">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          data-testid={`${testIdPrefix}-input`}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          inputMode="search"
          className="flex-1 bg-transparent outline-none px-3 text-sm placeholder:text-muted-foreground disabled:opacity-60"
        />
      </div>
      {open && (results.length > 0 || loading) && !disabled ? (
        <div
          data-testid={`${testIdPrefix}-results`}
          className="absolute z-[200] left-0 right-0 mt-1.5 rounded-xl border border-border/55 bg-popover/95 backdrop-blur-2xl overflow-hidden"
          style={{ boxShadow: "0 18px 44px -22px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.025) inset" }}
        >
          {!query.trim() ? (
            <div className="px-3.5 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 border-b border-border/40">
              Popular tickers
            </div>
          ) : null}
          <ul role="listbox" className="overflow-y-auto py-1 max-h-[240px]" style={{ WebkitOverflowScrolling: "touch" }}>
            {loading && results.length === 0 ? (
              <li className="px-3.5 py-2.5 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-loading`}>Loading…</li>
            ) : null}
            {results.map((r, i) => (
              <li
                key={`${r.ticker}-${i}`}
                role="option"
                aria-selected={i === highlight}
                data-testid={`${testIdPrefix}-result-${r.ticker}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => commit(r)}
                className={`px-3 py-2 cursor-pointer flex items-center justify-between gap-3 ${
                  i === highlight ? "bg-secondary/55" : "hover:bg-secondary/35"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-xs sm:text-sm w-14 shrink-0 tabular tracking-wider text-foreground/95">
                    {r.ticker}
                  </span>
                  <span className="text-sm truncate text-muted-foreground">{r.name}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 text-[11px] text-muted-foreground/75">
                  {r.exchange ? <span className="hidden sm:inline">{r.exchange}</span> : null}
                  {r.sector ? <span className="hidden md:inline">· {r.sector}</span> : null}
                </div>
              </li>
            ))}
            {!loading && query.trim() && results.length === 0 ? (
              <li className="px-3.5 py-2.5 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
                {emptyMessage}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
